import React, { FC, useRef, useEffect, useState, useCallback } from 'react';
import { Box, FormLabel, TextField, Typography } from '@mui/material';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { usePacContainerWidthSync } from '../../hooks/usePacContainerWidthSync';

import styles from './GooglePlacesInput.module.scss';
import './pacDropdown.module.scss';

interface GooglePlacesInputProps {
  inputLabel: string;
  value: string;
  name?: string;
  placeholder?: string;
  customClass?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  onChange: (address: string, placeDetails?: google.maps.places.PlaceResult) => void;
  onBlur?: () => void;
}

const GooglePlacesInput: FC<GooglePlacesInputProps> = ({
  inputLabel,
  value = '',
  name,
  placeholder = 'Enter address...',
  customClass,
  required = false,
  disabled = false,
  error = false,
  helperText,
  onChange,
  onBlur,
}) => {
  const { isLoaded } = useGoogleMaps();
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  usePacContainerWidthSync(inputRef, isLoaded && !disabled);

  const [inputValue, setInputValue] = useState(value);
  const isUpdatingFromGoogleRef = useRef(false);

  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
    }
  }, [value, inputValue]);

  const handlePlaceSelected = useCallback(() => {
    if (!autocompleteRef.current) {
      return;
    }

    const place = autocompleteRef.current.getPlace();

    if (place && place.formatted_address) {
      isUpdatingFromGoogleRef.current = true;
      setInputValue(place.formatted_address);
      onChange(place.formatted_address, place);

      setTimeout(() => {
        isUpdatingFromGoogleRef.current = false;
      }, 100);
    } else {
      console.log('Invalid place or no formatted_address');
    }
  }, [onChange]);

  useEffect(() => {
    if (!isLoaded || !inputRef.current || disabled || autocompleteRef.current) return;

    try {
      const options: google.maps.places.AutocompleteOptions = {
        types: ['address'],
        fields: ['address_components', 'formatted_address', 'geometry', 'place_id', 'name', 'types'],
      };

      autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, options);
      autocompleteRef.current.addListener('place_changed', handlePlaceSelected);
    } catch (error) {
      console.error('Failed to initialize Google Places Autocomplete:', error);
    }
  }, [isLoaded, disabled, handlePlaceSelected]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const pacContainer = document.querySelector('.pac-container');
      if (pacContainer?.contains(e.target as Node)) {
        setTimeout(() => {
          handlePlaceSelected();
        }, 300);
      }
    };

    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [handlePlaceSelected]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    if (!isUpdatingFromGoogleRef.current) {
      onChange(newValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const hasDropdown = document.querySelector('.pac-container:not(.pac-placeholder)');
      if (hasDropdown) {
        event.preventDefault();
      }
    }
  };

  const handleFocus = () => {
    inputRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  const handleBlur = () => {
    setTimeout(() => {
      onBlur?.();
    }, 200);
  };

  if (!isLoaded) {
    return (
      <Box className={`${styles.googlePlacesContainer} ${customClass || ''}`}>
        <FormLabel className={styles.inputLabel}>
          <Typography variant="h11Semibold">{inputLabel}</Typography>
        </FormLabel>
        <TextField
          disabled
          placeholder="Loading Google Maps..."
          className={styles.inputField}
          variant="outlined"
          fullWidth
          value=""
        />
      </Box>
    );
  }

  return (
    <Box className={`${styles.googlePlacesContainer} ${customClass || ''}`}>
      <FormLabel className={styles.inputLabel}>
        <Typography variant="h11Semibold">{inputLabel}</Typography>
      </FormLabel>
      <TextField
        inputRef={inputRef}
        name={name}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        error={error}
        helperText={helperText}
        className={`${styles.inputField} ${error ? styles.error : ''}`}
        variant="outlined"
        fullWidth
        InputLabelProps={{ shrink: false }}
        autoComplete="off"
      />
    </Box>
  );
};

export default GooglePlacesInput;
